/**
 * Utility functions for generating payment schedules
 */

import type { Payment } from './types';

interface PaymentScheduleParams {
  serviceCost: number;
  licenseCost: number;
  milestoneCount: 2 | 3 | 4;
}

/**
 * Generate license description for templates
 */
export function generateLicenseDescription(
  product: string,
  terms: string,
  users?: number,
  pricingTier?: string
): string {
  const userInfo = users 
    ? `${users} user${users > 1 ? 's' : ''}`
    : pricingTier || '';
  
  return `${product} (${terms}${userInfo ? ', ' + userInfo : ''})`;
}

/**
 * Generate standard payment schedule based on milestone count
 * 
 * 2 Milestones:
 *  - 60% services + 100% licensing
 *  - 40% services
 * 
 * 3 Milestones (Standard):
 *  - 50% services + 100% licensing
 *  - 40% services
 *  - 10% services
 * 
 * 4 Milestones:
 *  - 40% services + 100% licensing
 *  - 30% services
 *  - 20% services
 *  - 10% services
 */
export function generatePaymentSchedule(params: PaymentScheduleParams): Payment[] {
  const { serviceCost, licenseCost, milestoneCount } = params;
  const payments: Payment[] = [];

  if (milestoneCount === 2) {
    // 60/40 split
    const serviceDeposit = serviceCost * 0.60;
    const serviceFinal = serviceCost * 0.40;
    
    payments.push(
      {
        id: crypto.randomUUID(),
        milestone: 'Initial Deposit',
        description: '60% of services + 100% of licensing',
        amount: parseFloat((serviceDeposit + licenseCost).toFixed(2)),
        serviceAmount: parseFloat(serviceDeposit.toFixed(2)),
        licenseAmount: parseFloat(licenseCost.toFixed(2)),
      },
      {
        id: crypto.randomUUID(),
        milestone: 'Final Payment',
        description: '40% of services upon project completion',
        amount: parseFloat(serviceFinal.toFixed(2)),
        serviceAmount: parseFloat(serviceFinal.toFixed(2)),
        licenseAmount: 0,
      }
    );
  } else if (milestoneCount === 3) {
    // 50/40/10 split (Standard)
    const serviceDeposit = serviceCost * 0.50;
    const serviceMilestone1 = serviceCost * 0.40;
    const serviceFinal = serviceCost * 0.10;
    
    payments.push(
      {
        id: crypto.randomUUID(),
        milestone: 'Initial Deposit',
        description: '50% of services + 100% of licensing',
        amount: parseFloat((serviceDeposit + licenseCost).toFixed(2)),
        serviceAmount: parseFloat(serviceDeposit.toFixed(2)),
        licenseAmount: parseFloat(licenseCost.toFixed(2)),
      },
      {
        id: crypto.randomUUID(),
        milestone: 'Milestone 1',
        description: '40% of services upon UAT completion',
        amount: parseFloat(serviceMilestone1.toFixed(2)),
        serviceAmount: parseFloat(serviceMilestone1.toFixed(2)),
        licenseAmount: 0,
      },
      {
        id: crypto.randomUUID(),
        milestone: 'Final Payment',
        description: '10% of services upon project completion',
        amount: parseFloat(serviceFinal.toFixed(2)),
        serviceAmount: parseFloat(serviceFinal.toFixed(2)),
        licenseAmount: 0,
      }
    );
  } else if (milestoneCount === 4) {
    // 40/30/20/10 split
    const serviceDeposit = serviceCost * 0.40;
    const serviceMilestone1 = serviceCost * 0.30;
    const serviceMilestone2 = serviceCost * 0.20;
    const serviceFinal = serviceCost * 0.10;
    
    payments.push(
      {
        id: crypto.randomUUID(),
        milestone: 'Initial Deposit',
        description: '40% of services + 100% of licensing',
        amount: parseFloat((serviceDeposit + licenseCost).toFixed(2)),
        serviceAmount: parseFloat(serviceDeposit.toFixed(2)),
        licenseAmount: parseFloat(licenseCost.toFixed(2)),
      },
      {
        id: crypto.randomUUID(),
        milestone: 'Milestone 1',
        description: '30% of services upon workshops completion',
        amount: parseFloat(serviceMilestone1.toFixed(2)),
        serviceAmount: parseFloat(serviceMilestone1.toFixed(2)),
        licenseAmount: 0,
      },
      {
        id: crypto.randomUUID(),
        milestone: 'Milestone 2',
        description: '20% of services upon implementation complete',
        amount: parseFloat(serviceMilestone2.toFixed(2)),
        serviceAmount: parseFloat(serviceMilestone2.toFixed(2)),
        licenseAmount: 0,
      },
      {
        id: crypto.randomUUID(),
        milestone: 'Final Payment',
        description: '10% of services upon cutover & closeout',
        amount: parseFloat(serviceFinal.toFixed(2)),
        serviceAmount: parseFloat(serviceFinal.toFixed(2)),
        licenseAmount: 0,
      }
    );
  }

  return payments;
}

/**
 * Calculate total amount from payments
 */
export function calculateTotalAmount(payments: Payment[]): number {
  return payments.reduce((sum, p) => sum + (p.amount || 0), 0);
}

/**
 * Calculate total service cost from payments
 */
export function calculateTotalServiceCost(payments: Payment[]): number {
  return payments.reduce((sum, p) => sum + (p.serviceAmount || 0), 0);
}

/**
 * Calculate total license cost from payments
 */
export function calculateTotalLicenseCost(payments: Payment[]): number {
  return payments.reduce((sum, p) => sum + (p.licenseAmount || 0), 0);
}

